import { Badge } from "@/components/ui/badge";
import { Calendar, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";

const LiveMatchCard = ({ match }) => {
  const navigate = useNavigate();

  const getStatusBadge = () => {
    const status = match.status || "";
    if (status === "upcoming") {
      return (
        <Badge className="bg-blue-500/10 text-blue-500 font-medium px-3 py-1 rounded-full text-xs" data-testid={`match-status-${match.id}`}>
          Upcoming
        </Badge>
      );
    } else if (status === "completed") {
      return (
        <Badge className="bg-gray-500/10 text-gray-500 font-medium px-3 py-1 rounded-full text-xs" data-testid={`match-status-${match.id}`}>
          Completed
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-red-500/10 text-red-500 font-medium px-3 py-1 rounded-full text-xs flex items-center gap-1.5" data-testid={`match-status-${match.id}`}>
          <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>
          Live
        </Badge>
      );
    }
  };

  const getSportBadge = () => {
    const sport = match.sport || "Cricket";
    const colors = {
      Cricket: "text-green-600",
      Football: "text-blue-600",
      Badminton: "text-purple-600",
      Hockey: "text-orange-600",
      Tennis: "text-yellow-600",
      Kabaddi: "text-red-600",
      Athletics: "text-pink-600",
      Basketball: "text-amber-600",
    };
    return <span className={`text-xs font-semibold tracking-wider uppercase ${colors[sport] || "text-gray-500"}`}>{match.league || sport}</span>;
  };

  const handleClick = () => {
    navigate(`/match/${match.id}`);
  };

  // Render score based on data source
  const renderCricketScore = () => {
    if (match.score && match.score.length > 0) {
      return (
        <div className="space-y-2 mb-4">
          {match.score.map((inning, idx) => {
            const inningParts = inning.inning?.match(/^(.+?)\s*(inning[s]?\s*\d*)/i);
            const team = inningParts ? inningParts[1].trim() : inning.inning;
            const label = inningParts ? inningParts[2].trim() : "";
            return (
              <div key={idx} className="rounded-xl p-3" style={{ background: "var(--bg-card-inner)" }}>
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>{team}</p>
                    {label && <p className="text-[10px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{label}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold" style={{ color: "var(--text-primary)" }}>
                      {inning.r}{inning.w && inning.w !== "-" ? <span style={{ color: "var(--text-muted)" }}>/{inning.w}</span> : null}
                    </p>
                    {inning.o && inning.o !== "-" && (
                      <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>{inning.o} ov</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }
    // No teams (individual sports like Badminton, Athletics)
    const hasTeams = (match.homeTeam || match.teams?.[0]) && (match.awayTeam || match.teams?.[1]);
    if (!hasTeams) return null;
    // Team sports with scores
    if (match.homeScore !== null && match.homeScore !== undefined) {
      return (
        <div className="space-y-2 mb-4">
          <div className="rounded-xl p-3 flex justify-between items-center" style={{ background: "var(--bg-card-inner)" }}>
            <div className="flex items-center gap-2">
              {match.homeTeamBadge && <img src={match.homeTeamBadge} alt="" className="w-6 h-6 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />}
              <p className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>{match.homeTeam || match.teams?.[0]}</p>
            </div>
            <p className="text-xl font-bold" style={{ color: "var(--text-primary)" }}>{match.homeScore}</p>
          </div>
          <div className="rounded-xl p-3 flex justify-between items-center" style={{ background: "var(--bg-card-inner)" }}>
            <div className="flex items-center gap-2">
              {match.awayTeamBadge && <img src={match.awayTeamBadge} alt="" className="w-6 h-6 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />}
              <p className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>{match.awayTeam || match.teams?.[1]}</p>
            </div>
            <p className="text-xl font-bold" style={{ color: "var(--text-primary)" }}>{match.awayScore}</p>
          </div>
        </div>
      );
    }
    // Team sports without scores - show VS
    return (
      <div className="rounded-xl p-4 mb-4 flex justify-between items-center" style={{ background: "var(--bg-card-inner)" }}>
        <span className="font-semibold" style={{ color: "var(--text-primary)" }}>{match.teams?.[0] || match.homeTeam}</span>
        <span className="text-lg font-bold" style={{ color: "var(--text-muted)" }}>VS</span>
        <span className="font-semibold" style={{ color: "var(--text-primary)" }}>{match.teams?.[1] || match.awayTeam}</span>
      </div>
    );
  };

  return (
    <div
      className="rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer overflow-hidden"
      style={{ background: "var(--bg-card)", border: "1px solid var(--border-color)" }}
      data-testid={`match-card-${match.id}`}
      onClick={handleClick}
    >
      <div className="p-3 sm:p-5">
        <div className="flex justify-between items-center mb-3 sm:mb-4">
          {getSportBadge()}
          {getStatusBadge()}
        </div>
        {(() => {
          const nameParts = match.name?.split(', ') || [];
          const title = nameParts[0] || match.name;
          const subtext = nameParts.length > 1 ? nameParts.slice(1).join(', ') : '';
          return (
            <>
              <h3 className="text-sm sm:text-base font-semibold leading-tight" style={{ color: "var(--text-primary)" }} data-testid={`match-name-${match.id}`}>
                {title}
              </h3>
              {subtext && (
                <p className="text-[10px] sm:text-xs mt-1 mb-3 sm:mb-4" style={{ color: "var(--text-muted)" }} data-testid={`match-subtext-${match.id}`}>
                  {subtext}
                </p>
              )}
              {!subtext && <div className="mb-3 sm:mb-4" />}
            </>
          );
        })()}
        {renderCricketScore()}
        {/* Status text for completed */}
        {match.statusText && match.status === "completed" && (
          <div className="rounded-lg p-3 mb-4" style={{ background: "rgba(0,191,99,0.08)" }}>
            <p className="text-xs font-medium text-[#00bf63]" data-testid={`match-result-${match.id}`}>
              {match.statusText}
            </p>
          </div>
        )}
        {/* Footer */}
        <div className="flex flex-wrap gap-3 text-xs pt-3" style={{ color: "var(--text-muted)", borderTop: "1px solid var(--border-color)" }}>
          {match.date && (
            <div className="flex items-center gap-1" data-testid={`match-date-${match.id}`}>
              <Calendar className="w-3 h-3" />
              <span>{new Date(match.dateTimeGMT || match.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
            </div>
          )}
          {match.venue && (
            <div className="flex items-center gap-1" data-testid={`match-venue-${match.id}`}>
              <MapPin className="w-3 h-3" />
              <span className="truncate max-w-[200px]">{match.venue}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveMatchCard;
